import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { useCity } from "@/contexts/CityContext";
import { BriefSubscribe } from "@/components/BriefSubscribe";
import { PublicShell } from "@/components/layout/PublicShell";
import { Button, Notice, Panel, SelectField, TextAreaField, TextField } from "@/components/system";
import { InviteColleague } from "@/components/InviteColleague";
import { ListingCard } from "@/components/ListingCard";
import { ButtonLink, SectionHead } from "@/components/system";
import { useAudience } from "@/contexts/AudienceContext";
import { SEO } from "@/lib/seo";
import {
  usePublicDirectory,
  usePublicSpecialties,
  type DirectoryMeta,
  type Facility,
} from "@/lib/public-directory";
import { formatRelativeAlertTime, usePublicAlerts } from "@/lib/public-alerts";
import { buildBulletin } from "@/lib/bulletin";
import { MorningBulletin } from "@/components/home/MorningBulletin";
import {
  submitPublicLead,
  type PublicLeadIntent,
} from "@/lib/public-contact";
import { track } from "@/lib/analytics";
import "@/styles/brand-a.css";

const CONTACT_EMAIL = "hello@pulseboard.com.au";

function isPublicLeadIntent(value: string | null): value is PublicLeadIntent {
  return (
    value === "request_access" ||
    value === "contact"
  );
}





const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
const GOOGLE_MAPS_MAP_ID = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID;

declare global {
  interface Window {
    google?: any;
    __pulseboardGoogleMapsPromise?: Promise<void>;
  }
}

export default function Home() {
  const { city, link: cityLink } = useCity();
  const navigate = useNavigate();
  const [searchQ, setSearchQ] = useState("");
  const [leadIntent, setLeadIntent] = useState<PublicLeadIntent>("request_access");
  const directoryPath = cityLink("/directory");
  const publicHealthAlertQuery = usePublicAlerts(city.slug, 10, {
    category: "public_health",
  });
  const directoryMetaQuery = usePublicDirectory(city.slug, { limit: 1 });
  const directoryMeta = directoryMetaQuery.data?.meta;
  const { isGpMode } = useAudience();
  const specialties = usePublicSpecialties(city.slug);
  const featuredQuery = usePublicDirectory(city.slug, { type: "specialist", limit: 12 });
  const featured = (featuredQuery.data?.items ?? []).slice(0, 3);
  const specialistCount = directoryMeta?.specialistCount;
  const topSpecialties = [...(specialties.data ?? [])].sort((x, y) => y.count - x.count).slice(0, 5);
  const publicHealthAlerts = (publicHealthAlertQuery.data?.items ?? []).filter((alert) => alert.category === "public_health");
  // Practice-confirmed only: acceptingCount counts availability a practice has published.
  const acceptingTotal = specialties.data?.reduce((sum, item) => sum + (item.acceptingCount ?? 0), 0);
  const bulletin = buildBulletin({
    notices: !publicHealthAlertQuery.data ? null : publicHealthAlerts.map((alert) => ({
      title: alert.title,
      summary: alert.summary,
      level: alert.level,
      publishedAt: alert.published_at,
      sourceUrl: alert.source_url,
      isCurrent: alert.is_current,
    })),
    specialistCount: specialistCount ?? null,
    acceptingCount: acceptingTotal ?? null,
    now: new Date(),
  });

  const goToDirectorySearch = (query: string, specialty = "") => {
    const q = query.trim();
    track("search", { query_length: q.length, specialty });
    navigate(`${directoryPath}${q ? `?q=${encodeURIComponent(q)}` : ""}`);
  };

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    goToDirectorySearch(searchQ);
  };

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  useEffect(() => {
    if (window.location.hash !== "#pulseboard-contact") return;

    const intent = new URLSearchParams(window.location.search).get("intent");
    if (isPublicLeadIntent(intent)) {
      setLeadIntent(intent);
    }

    window.setTimeout(() => {
      scrollToSection("pulseboard-contact");
      document.getElementById("pulseboard-lead-email")?.focus();
    }, 0);
  }, []);

  return (
    <>
      <SEO
        title={`PulseBoard ${city.name} — GP-specialist referral directory`}
        description={`Source-attributed specialist and allied health listings, public health alerts, and clinical tools for ${city.name} clinicians.`}
        path="/"
      />

      <PublicShell>
        <div className="ds-container">
          <MorningBulletin
            cityName={city.name}
            bulletin={bulletin}
            updatedLabel={
              publicHealthAlertQuery.data?.meta.lastSuccessAt
                ? `Notices checked ${formatRelativeAlertTime(publicHealthAlertQuery.data.meta.lastSuccessAt)}`
                : null
            }
            acceptingCount={acceptingTotal ?? null}
            noticesState={publicHealthAlertQuery.isLoading ? "loading" : publicHealthAlertQuery.isError ? "error" : "ready"}
          />

          <section className="ds-section ds-find" aria-labelledby="home-find">
            <SectionHead
              id="home-find"
              eyebrow={isGpMode ? `For ${city.name} GPs` : `${city.name} specialist directory`}
              title={isGpMode ? "Refer with confidence." : "Find a specialist"}
              lead={
                isGpMode
                  ? "Referral requirements, availability and contact details, with the source shown for every field."
                  : "Every listing shows where its details came from and when they were last checked."
              }
            />
            <form className="ds-hero__search" role="search" onSubmit={handleSearch}>
              <label htmlFor="home-search" className="ds-visually-hidden">Search specialists</label>
              <Search aria-hidden="true" />
              <input
                id="home-search"
                type="search"
                value={searchQ}
                onChange={(event) => setSearchQ(event.target.value)}
                placeholder="Specialty, doctor, practice or suburb"
              />
              <Button type="submit" size="lg">Search</Button>
            </form>
            <ul className="ds-cluster ds-hero__chips" aria-label="Popular specialties" style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {topSpecialties.map((item) => (
                <li key={item.slug}><Link className="ds-chip" to={`/directory/${item.slug}`}>{item.name}</Link></li>
              ))}
              <li><Link className="ds-chip" to="/conditions">Which specialist do I need?</Link></li>
            </ul>
          </section>

          {specialties.data && specialties.data.length > 0 ? (
            <section className="ds-section" aria-labelledby="home-specialties">
              <SectionHead
                id="home-specialties"
                eyebrow="Browse"
                title="Specialists by specialty"
                actions={<ButtonLink to="/directory" variant="secondary">Full directory</ButtonLink>}
              />
              <ul className="ds-link-grid">
                {specialties.data.map((item) => (
                  <li key={item.slug}>
                    <Link to={`/directory/${item.slug}`}>
                      <span>{item.name}</span>
                      <span className="ds-num">{item.count}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {featured.length > 0 ? (
            <section className="ds-section" aria-labelledby="home-featured">
              <SectionHead
                id="home-featured"
                eyebrow="From the directory"
                title="Source-checked listings"
                lead="Availability appears only when a practice has confirmed it."
              />
              <div className="ds-grid">
                {featured.map((facility) => <ListingCard key={facility.id} facility={facility} />)}
              </div>
            </section>
          ) : null}

          <section className="ds-section" aria-labelledby="canberra-map-title">
            <div className="ds-split">
              <CanberraMap citySlug={city.slug} directoryMeta={directoryMeta} />
              <InviteColleague />
            </div>
          </section>

          <HomeContact citySlug={city.slug} leadIntent={leadIntent} onLeadIntentChange={setLeadIntent} />
        </div>
      </PublicShell>
    </>
  );
}

function CanberraMap({
  citySlug,
  directoryMeta,
}: {
  citySlug: string;
  directoryMeta: DirectoryMeta | undefined;
}) {
  const { data: gpData } = usePublicDirectory(citySlug, {
    type: "gp_practice",
    limit: 18,
  });
  const { data: specialistData } = usePublicDirectory(citySlug, {
    type: "specialist",
    limit: 10,
  });
  const { data: alliedData } = usePublicDirectory(citySlug, {
    type: "allied_health",
    limit: 10,
  });
  const facilities = useMemo(
    () =>
      dedupeMapFacilities([
        ...(gpData?.items ?? []),
        ...(specialistData?.items ?? []),
        ...(alliedData?.items ?? []),
      ]),
    [alliedData?.items, gpData?.items, specialistData?.items],
  );
  return (
    <section className="ds-panel ds-map-panel" aria-labelledby="canberra-map-title">
      <div className="ds-stack" style={{ gap: 8 }}>
        <p className="ds-eyebrow ds-eyebrow--teal">Network</p>
        <h2 id="canberra-map-title" className="ds-title">Where listed practices are</h2>
        <p className="ds-small">GP practices, specialists and allied health across {directoryMeta ? `${directoryMeta.suburbCount} Canberra suburbs` : "Canberra"}.</p>
      </div>
      <div className="pb-map">
        <GoogleCanberraMap facilities={facilities} />
        <div className="pb-map-wash" aria-hidden="true" />
        <div className="pb-map-legend">
          <MapLegendItem
            tone="teal"
            label={directoryMeta ? `${directoryMeta.gpCount} GP practices` : "GP practices"}
          />
          <MapLegendItem
            tone="violet"
            label={directoryMeta ? `${directoryMeta.specialistCount} specialists` : "Specialists"}
          />
          <MapLegendItem
            tone="amber"
            label={directoryMeta ? `${directoryMeta.alliedCount} allied health` : "Allied health"}
          />
        </div>
      </div>
    </section>
  );
}

function GoogleCanberraMap({ facilities }: { facilities: Facility[] }) {
  const mapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY || !mapRef.current) return;

    let cancelled = false;

    loadGoogleMaps(GOOGLE_MAPS_API_KEY)
      .then(() => {
        if (cancelled || !mapRef.current || !hasGoogleMapsCore()) return;
        const maps = window.google.maps;
        mapRef.current.replaceChildren();
        const map = new maps.Map(mapRef.current, {
          center: { lat: -35.292, lng: 149.126 },
          zoom: 11.35,
          disableDefaultUI: true,
          clickableIcons: false,
          gestureHandling: "none",
          keyboardShortcuts: false,
          backgroundColor: "#f4f0e7",
          mapId: GOOGLE_MAPS_MAP_ID || undefined,
          styles: GOOGLE_MAPS_MAP_ID ? undefined : GOOGLE_MAP_STYLES,
        });

        const geocoder = new maps.Geocoder();
        const bounds = new maps.LatLngBounds();
        const geocodeTargets = facilities
          .map((facility) => ({
            facility,
            address: facilityMapAddress(facility),
          }))
          .filter((item): item is { facility: Facility; address: string } =>
            Boolean(item.address),
          )
          .slice(0, 32);

        geocodeTargets.forEach(({ facility, address }) => {
          geocoder.geocode(
            {
              address,
              componentRestrictions: {
                country: "AU",
                administrativeArea: "ACT",
              },
            },
            (results: any, status: string) => {
              if (cancelled || status !== "OK" || !results?.[0]?.geometry?.location) {
                return;
              }

              const position = results[0].geometry.location;
              bounds.extend(position);
              new maps.Marker({
                position,
                map,
                title: facility.name,
                icon: {
                  url: markerSvg(markerToneForFacility(facility)),
                  scaledSize: new maps.Size(24, 24),
                  anchor: new maps.Point(12, 12),
                },
              });

              if (!bounds.isEmpty()) {
                map.fitBounds(bounds, 62);
              }
            },
          );
        });
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [facilities]);

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <div className="pb-map-config-state">
        <strong>Network map unavailable</strong>
        <span>Use the directory to browse source-attributed provider locations.</span>
      </div>
    );
  }

  return <div ref={mapRef} className="pb-google-map-canvas" aria-label="Google map of Canberra" />;
}

function loadGoogleMaps(apiKey: string): Promise<void> {
  if (hasGoogleMapsCore()) return Promise.resolve();
  if (window.__pulseboardGoogleMapsPromise) return window.__pulseboardGoogleMapsPromise;

  window.__pulseboardGoogleMapsPromise = new Promise((resolve) => {
    const callbackName = "__pulseboardInitGoogleMaps";
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-pulseboard-google-maps="true"]',
    );
    let settled = false;
    let attempts = 0;
    let pollId: number | undefined;

    const resolveIfReady = () => {
      if (settled || !hasGoogleMapsCore()) return;
      settled = true;
      if (pollId) window.clearInterval(pollId);
      resolve();
    };

    (window as unknown as Record<string, unknown>)[callbackName] = resolveIfReady;

    pollId = window.setInterval(() => {
      attempts += 1;
      resolveIfReady();
      if (!settled && attempts >= 50) {
        window.clearInterval(pollId);
      }
    }, 200);

    if (existing) {
      resolveIfReady();
      return;
    }

    const script = document.createElement("script");
    script.dataset.pulseboardGoogleMaps = "true";
    script.async = true;
    script.onload = resolveIfReady;
    script.onerror = resolveIfReady;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey,
    )}&v=weekly&loading=async&callback=${callbackName}`;
    document.head.appendChild(script);
  });

  return window.__pulseboardGoogleMapsPromise;
}

function hasGoogleMapsCore(): boolean {
  const maps = window.google?.maps;

  return Boolean(
    maps &&
      typeof maps.Map === "function" &&
      typeof maps.Geocoder === "function" &&
      typeof maps.LatLngBounds === "function" &&
      typeof maps.Marker === "function" &&
      typeof maps.Size === "function" &&
      typeof maps.Point === "function",
  );
}

function dedupeMapFacilities(items: Facility[]): Facility[] {
  const seen = new Set<string>();

  return items.filter((facility) => {
    const address = facilityMapAddress(facility);
    if (!address) return false;

    const key = `${facility.facility_type}:${address.toLowerCase()}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function markerToneForFacility(facility: Facility): string {
  if (facility.facility_type === "specialist") return "violet";
  if (facility.facility_type === "allied_health") return "amber";
  return "teal";
}

function markerSvg(tone: string): string {
  const colors: Record<string, string> = {
    amber: "#f0a42d",
    blue: "#9bc9d8",
    coral: "#ec6656",
    green: "#64bd62",
    magenta: "#bf5ba0",
    teal: "#2b8c8d",
    violet: "#7564a7",
  };
  const fill = colors[tone] ?? colors.teal ?? "#2b8c8d";
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34"><filter id="s" x="-60%" y="-60%" width="220%" height="220%"><feDropShadow dx="0" dy="4" stdDeviation="3" flood-color="#203430" flood-opacity=".2"/></filter><circle cx="17" cy="17" r="9.5" fill="${fill}" stroke="rgba(255,255,255,.96)" stroke-width="4" filter="url(#s)"/><circle cx="17" cy="17" r="14.5" fill="none" stroke="${fill}" stroke-opacity=".18" stroke-width="2.5"/></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function facilityMapAddress(facility: Facility): string | null {
  const address = facility.address_json;
  if (address.full_address) return address.full_address;
  if (!address.street) return null;

  const parts = [
    address.street,
    address.suburb,
    address.state ?? "ACT",
    address.postcode,
    "Australia",
  ].filter(Boolean);

  if (parts.length > 2) return parts.join(", ");
  return null;
}

const GOOGLE_MAP_STYLES = [
  { featureType: "all", elementType: "geometry", stylers: [{ saturation: -34 }, { lightness: 22 }] },
  { featureType: "all", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "all", elementType: "labels.text.fill", stylers: [{ color: "#6c7971" }] },
  { featureType: "all", elementType: "labels.text.stroke", stylers: [{ color: "#fbf8f1" }, { weight: 3 }] },
  { featureType: "poi.business", stylers: [{ visibility: "off" }] },
  { featureType: "poi", elementType: "labels.text", stylers: [{ visibility: "simplified" }, { lightness: 26 }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }, { lightness: 8 }] },
  { featureType: "road", elementType: "labels", stylers: [{ lightness: 28 }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#eadba7" }, { weight: 1 }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#e3cf8f" }, { weight: 1.15 }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#d4e9ed" }, { lightness: 8 }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#e7f2dd" }] },
  { featureType: "landscape.man_made", elementType: "geometry", stylers: [{ color: "#f5f1e8" }] },
] as const;

function MapLegendItem({ tone, label }: { tone: string; label: string }) {
  return (
    <div>
      <span className={`pb-legend-dot pb-legend-dot--${tone}`} />
      {label}
    </div>
  );
}

function HomeContact({
  citySlug,
  leadIntent,
  onLeadIntentChange,
}: {
  citySlug: string;
  leadIntent: PublicLeadIntent;
  onLeadIntentChange: (intent: PublicLeadIntent) => void;
}) {
  return (
    <section id="about" className="ds-section" aria-label="Contact PulseBoard">
      <div className="ds-split" id="pulseboard-contact">
        <LeadCaptureForm citySlug={citySlug} intent={leadIntent} onIntentChange={onLeadIntentChange} />
        <BriefSubscribe sourcePath="/" />
      </div>
    </section>
  );
}

function LeadCaptureForm({
  citySlug,
  intent,
  onIntentChange,
}: {
  citySlug: string;
  intent: PublicLeadIntent;
  onIntentChange: (intent: PublicLeadIntent) => void;
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [organisation, setOrganisation] = useState("");
  const [message, setMessage] = useState("");
  const [state, setState] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setState("submitting");
    try {
      await submitPublicLead(citySlug, {
        intent,
        email,
        name: name || undefined,
        organisation: organisation || undefined,
        message: message || undefined,
        sourcePath: `${window.location.pathname}${window.location.search}`,
      });
      track("access_request", { intent });
      setState("success");
      setEmail("");
      setName("");
      setOrganisation("");
      setMessage("");
    } catch {
      setState("error");
    }
  };

  return (
    <Panel aria-labelledby="home-lead-heading">
      <div className="ds-stack" style={{ gap: 8 }}>
        <p className="ds-eyebrow ds-eyebrow--teal">Pilot access</p>
        <h2 id="home-lead-heading" className="ds-title">Request access or get in touch</h2>
        <p className="ds-small">Send your details and we will reply directly.</p>
      </div>
      {state === "success" ? (
        <Notice tone="success">Thank you. Your request has been received.</Notice>
      ) : (
        <form className="ds-stack" style={{ gap: 16 }} onSubmit={handleSubmit}>
          <div className="ds-form-grid">
            <SelectField label="Reason" value={intent} onChange={(event) => onIntentChange(event.target.value as PublicLeadIntent)}>
              <option value="request_access">Request access</option>
              <option value="contact">Contact PulseBoard</option>
            </SelectField>
            <TextField
              id="pulseboard-lead-email"
              label="Email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@practice.com.au"
            />
            <TextField label="Name" autoComplete="name" value={name} onChange={(event) => setName(event.target.value)} placeholder="Optional" />
            <TextField label="Practice or organisation" autoComplete="organization" value={organisation} onChange={(event) => setOrganisation(event.target.value)} placeholder="Optional" />
            <TextAreaField full label="Message" value={message} onChange={(event) => setMessage(event.target.value)} placeholder="Optional" rows={3} />
          </div>
          {state === "error" ? (
            <Notice tone="error">
              The request didn't send. Try again, or email <span className="ds-num">{CONTACT_EMAIL}</span>.
            </Notice>
          ) : null}
          <div className="ds-cluster" style={{ justifyContent: "space-between" }}>
            <p className="ds-fine">
              By sending this request you agree to the <Link className="ds-link" to="/privacy">Privacy Policy</Link> and{" "}
              <Link className="ds-link" to="/terms">Terms of Service</Link>.
            </p>
            <Button type="submit" disabled={state === "submitting"}>
              {state === "submitting" ? "Sending…" : "Send request"}
            </Button>
          </div>
        </form>
      )}
    </Panel>
  );
}


